/**
 * Page that, when visited, initiates log in
 */
import React from 'react';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/styles';
import classnames from 'classnames';
import { Button } from '@material-ui/core';

const propTypes = {
  className: PropTypes.shape({}),
  activeMessage: PropTypes.shape({
    message: PropTypes.string,
    options: PropTypes.shape({}),
    title: PropTypes.string,
  }),
  classes: PropTypes.shape({}).isRequired,
  removeBanner: PropTypes.func.isRequired,
  activateBanner: PropTypes.func.isRequired,
  enqueueBanner: PropTypes.func.isRequired,
  setFreeCreditAware: PropTypes.func.isRequired,
  bannerNotifications: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  org: PropTypes.shape({}),
  freeCreditAware: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    impersonatedOrganizationId: PropTypes.string,
  }).isRequired,
  stopOrgImpersonation: PropTypes.func.isRequired,
  getOrg: PropTypes.func.isRequired,
  voidPosition: PropTypes.bool,
};

const defaultProps = {
  activeMessage: null,
  className: null,
  org: null,
  voidPosition: false,
};

function BuildBillingBanner(org, removeBanner) {
  if (org.billingType !== 1 || !org.trialCreditCents || org.trialCreditCents === 0) {
    return null;
  }
  const creditValue = parseInt(org.trialCreditCents, 10) / 100;
  const temp = {
    type: 'freeCredit',
    title: 'Congratulations! ', // space is required at the end
    message: `You have $${creditValue} in credit available to use towards your account. `,
    options: {
      DISMISS: removeBanner,
    },
  };
  return temp;
}

function BuildImpersonationBanner(org) {
  const temp = {
    type: 'impersonation',
    title: '',
    message: 'You are in impersonation Mode for ',
    companyName: org ? org.companyName || 'N/A' : 'N/A',
    options: {},
  };
  return temp;
}

export function UnstyledBanner({
  classes,
  bannerNotifications,
  activeMessage,
  activateBanner,
  enqueueBanner,
  removeBanner,
  freeCreditAware,
  setFreeCreditAware,
  org,
  user,
  stopOrgImpersonation,
  getOrg,
  voidPosition,
}) {
  const handleStopClick = () => {
    stopOrgImpersonation();
    getOrg();
  };

  function GenOptions(color) {
    const toRet = [];
    const optionsIndex = Object.keys(activeMessage.options);
    optionsIndex.forEach((key) => {
      toRet.push(
        <Button
          key={key}
          name="option"
          className={color}
          onClick={(event) => {
            event.preventDefault();
            activeMessage.options[key](event);
          }}
        >
          {key}
        </Button>,
      );
    });
    return toRet;
  }

  if (!freeCreditAware && org) {
    const msg = BuildBillingBanner(org, removeBanner);
    if (msg) {
      setFreeCreditAware();
      enqueueBanner(msg); // This could be anywhere in your app.
    }
  }

  if (bannerNotifications.length > 0 && activeMessage === null) {
    activateBanner();
  }

  let bannerClass = classnames(classes.bannerFreeCredit);
  let msg = null;

  if (user.impersonatedOrganizationId) {
    msg = BuildImpersonationBanner(org);
    bannerClass = classnames(classes.bannerFreeCredit, classes.bannerFreeCreditPlusImper);
  }

  let position = {};
  if (voidPosition) {
    position = { position: 'unset' };
  }

  return (
    <React.Fragment>
      {msg && (
        <Paper className={classes.bannerImpersonation} style={position}>
          <i className={classnames('material-icons', classes.user)}>face</i>
          <div className={classes.blockImpersonation}>
            <div className={classes.messageImpersonation}>
              {msg.message}
              <div className={classes.companyName}>{msg.companyName}</div>
            </div>
          </div>
          <i
            name="stop"
            role="button"
            tabIndex="0"
            className={classnames('material-icons', classes.rightIcon)}
            onClick={handleStopClick}
            onKeyDown={() => handleStopClick()}
          >
            cancel
          </i>
        </Paper>
      )}

      {activeMessage && (
        <Paper className={bannerClass}>
          <i className={classnames('material-icons', classes.cake)}>cake</i>
          <div className={classes.blockFreeCredit}>
            <div className={classes.title}>{activeMessage.title}</div>
            <div className={classes.messageFreeCredit}>
              <div className={classnames(classes.title, classes.titlesm)}>
                {activeMessage.title}
              </div>
              {activeMessage.message}
              {GenOptions(classes.option)}
            </div>
          </div>
        </Paper>
      )}
    </React.Fragment>
  );
}

UnstyledBanner.propTypes = propTypes;
UnstyledBanner.defaultProps = defaultProps;

const freeCreditBannerHeight = 45;
const impersonationBannerHeight = 45;
const styles = (theme) => ({
  bannerFreeCredit: {
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'flex-start',
    top: '0px',
    height: freeCreditBannerHeight,
    width: '100%',
    backgroundColor: theme.customColors.teal,
    color: theme.customColors.white,
    zIndex: theme.zIndex.drawer + 2, // Stay above drawer!
    borderRadius: '0px',
    [theme.breakpoints.down('sm')]: {
      position: 'fixed', // Avoid scrolling when in a cellphone size(like the header)
    },
  },
  bannerFreeCreditPlusImper: {
    top: impersonationBannerHeight,
  },
  bannerImpersonation: {
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'flex-start',
    top: '0px',
    height: impersonationBannerHeight,
    width: '100%',
    backgroundColor: theme.customColors.orange,
    color: theme.customColors.white,
    zIndex: theme.zIndex.drawer + 2,
    borderRadius: '0px',
    [theme.breakpoints.down('sm')]: {
      position: 'fixed', // Avoid scrolling when in a cellphone size(like the header)
    },
  },
  cake: {
    fontSize: 30,
    marginLeft: 8,
    marginRight: 7,
  },
  user: {
    fontSize: 30,
    marginLeft: 8,
    marginRight: 7,
  },
  blockFreeCredit: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  blockImpersonation: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  title: {
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    fontSize: 12,
    fontWeight: 900,
    lineHeight: '0.8em',
    marginTop: 8,
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      display: 'None',
    },
  },
  rightIcon: {
    fontSize: 24,
    color: theme.customColors.white,
    marginLeft: 'auto',
    paddingRight: '5px',
    cursor: 'pointer',
  },
  titlesm: {
    [theme.breakpoints.down('sm')]: {
      display: 'contents',
    },
    [theme.breakpoints.up('md')]: {
      display: 'None',
    },
  },
  messageImpersonation: {
    display: 'flex',
    alignContent: 'space-between',
    flexWrap: 'wrap',
    overflowWrap: 'break-word',
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '13px',
  },
  messageFreeCredit: {
    display: 'flex',
    alignContent: 'space-between',
    flexWrap: 'wrap',
    overflowWrap: 'break-word',
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    fontSize: 12,
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      lineHeight: '13px',
    },
  },
  companyName: {
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    fontSize: 12,
    fontWeight: 900,
    marginLeft: 4,
    display: 'contents',
    [theme.breakpoints.down('sm')]: {
      lineHeight: '13px',
    },
  },
  option: {
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    fontSize: 12,
    fontWeight: 900,
    zIndex: theme.zIndex.drawer + 3,
    paddingLeft: 5,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    minWidth: '0px',
    color: 'white',
    textDecoration: 'underline',
    cursor: 'pointer',
    display: 'contents',
    lineHeight: '13px',
  },
});

export default withStyles(styles)(UnstyledBanner);
